import { html } from "lit-html";
import { HeartBank } from "../managers/HeartBank.ts";
import { Crypto } from "../models/Crypto.ts";
import { getDeviceID } from "../components/global.ts";
import { Me } from "./Me.ts";

// for decrypting location, message, diamonds
export class Chest {
    private heartbank = new HeartBank();
    private crypto = new Crypto();
    private me = new Me();
    private did = getDeviceID();

    private chest: any;
    private data: any;
    public timelock: Date | null;

    constructor(chest: any, data: any) {
        this.chest = chest;
        this.data = data;
        this.timelock = this.data.b.t ? new Date(this.data.b.t) : null;
    }

    isTimeLocked(): boolean {
        return this.timelock ? new Date() < this.timelock : false;
    }

    async getNameI() {
        return this.chest.type === "mint" ||
            this.chest.uid === this.heartbank.user?.uid
            ? "I"
            : await this.me.getName(this.data.f.p);
    }

    async getNameMy() {
        return this.chest.type === "mint" ||
            this.chest.uid === this.heartbank.user?.uid
            ? "my"
            : (await this.me.getName(this.data.f.p)) + "'s";
    }

    getPlace() {
        return this.data.l.n.endsWith(".")
            ? this.data.l.n
            : this.data.l.n + ".";
    }

    getTitle(type?: "courier" | "timelock" | "both") {
        const heading =
            type === "both"
                ? `ANONYMOUS COURIER DELIVERY\n\n🔒 TIME LOCK UNTIL: ${this.timelock?.toFormattedString()}`
                : type === "courier"
                ? "ANONYMOUS COURIER DELIVERY"
                : type === "timelock"
                ? `🔒 TIME LOCK UNTIL: ${this.timelock?.toFormattedString()}`
                : this.data.b.v === 0
                ? "AQUARIUS GLOBAL BLOCKCHAIN"
                : "END-TO-END ENCRYPTED COMMUNICATION";
        const karat = this.data.n.v
            ? `💛 ${this.data.b.w || 0} Karat Gold`
            : `🩶 ${this.data.b.w || 0} Karat Silver`;
        const carat = this.data.b.d
            ? ` • 💎 ${this.data.b.d} Carat Diamond`
            : "";
        const gratuity =
            this.data.g && this.data.g.a > 0
                ? ` • 💵 ${
                      type === "both" || type === "timelock"
                          ? "$$$"
                          : this.data.g.a.toCashAmount()
                  } Cash ${this.data.g.c}`
                : "";
        return `${heading}\n\nHeartBank® Chest: ${karat + carat + gratuity} • ${
            this.chest.name
        }`;
    }

    async getTimeLockText(isPublic = true) {
        if (isPublic) {
            const i = await this.getNameI();
            const my = await this.getNameMy();
            return `${i} wrote a time-locked message for Someone!\n\nTap on the link below to see ${my} Countdown in real time.`;
        } else
            return "There's a time-locked message for Someone!\n\nTap on the link below to see the Countdown in real time.";
    }

    async getPublicText(storage: "cloud" | "code" | "crystal") {
        const name = await this.getNameI();
        const names = await this.me.getNames(this.data.t);
        return (
            `${name} wrote ${
                this.data.b.i ? "an encrypted" : "a public"
            } message inside for ${names}!` + (await this.getInfo(storage))
        );
    }

    async getAnonymousText(storage: "cloud" | "code" | "crystal") {
        const name = await this.me.getName(this.data.f.p);
        const names = await this.me.getNames(this.data.t);
        return (
            `There's ${
                this.data.b.i ? "an encrypted" : "a public"
            } message inside for ${names} from ${name} at ${this.getPlace()}` +
            (await this.getInfo(storage, true))
        );
    }

    private async getInfo(
        storage: "cloud" | "code" | "crystal",
        isAnonymous = false
    ) {
        const name = isAnonymous ? "this" : await this.getNameMy();
        switch (storage) {
            case "cloud":
                return `\n\nTap on the link below to open ${name} Silver B-Chest.`;
            case "code":
                return `\n\nTap on the link below to upload ${name} Silver QR Code.`;
            case "crystal":
                return `\n\nTap on the link below to upload ${name} Silver 3B Crystal.`;
        }
    }

    // secure
    private async isValidLocker() {
        // from
        if (await this.crypto.isValid2(this.data.f.p)) return true;
        // to
        if (this.chest.recipients)
            for (const recipient of this.chest.recipients) {
                if (await this.crypto.isValid2(recipient)) return true;
            }
        return false;
    }

    // not secure
    private async isValidLocker2() {
        // from and to
        return (
            (await this.crypto.isValid2(this.data.f.p)) ||
            this.chest.devices?.includes(this.did)
        );
    }

    private async isValid() {
        // from and to
        if (await this.isValidLocker()) return true;
        // from and to
        return (
            this.heartbank.user &&
            (this.chest.uid === this.heartbank.user.uid ||
                this.chest.users?.includes(this.heartbank.user.uid))
        );
        // this.me.getSafe(this.heartbank.user.uid, this.data.f.p)
    }

    async getMessage() {
        let message: string | null;
        if (this.data.b.v === 0) {
            if (this.data.n.h) {
                const isPublic =
                    this.heartbank.user &&
                    !this.data.n.e &&
                    this.data.b.e === 1;
                message =
                    isPublic || (await this.isValid())
                        ? this.chest.message
                        : null;
            } else message = this.data.n.m;
        } else message = this.data.n.h ? this.chest.message : this.data.n.m;
        return message;
    }

    async getLocation(): Promise<{ lat: number; lng: number }> {
        let location: any;
        if (this.data.b.v === 0) {
            if (this.data.l.h) {
                const isPublic =
                    this.heartbank.user &&
                    !this.data.l.e &&
                    this.data.b.e === 1;
                location =
                    isPublic || (await this.isValid())
                        ? this.chest.location
                        : null;
            } else location = this.data.l;
        } else location = this.data.l.h ? this.chest.location : this.data.l;
        return location
            ? { lat: location.t, lng: location.g }
            : { lat: this.data.l.y, lng: this.data.l.x };
    }

    async renderLocation() {
        const location = await this.getLocation();
        return html`
            <a
                href="${`https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`}"
                target="_blank">
                ${this.data.l.n}
            </a>
        `;

        // return location
        //     ? html`
        //           <a
        //               href="${`https://www.google.com/maps/search/?api=1&query=${location.t},${location.g}`}"
        //               target="_blank">
        //               ${this.data.l.n}
        //           </a>
        //       `
        //     : html`<span>${this.data.l.n}</span>`;
        // html`
        //       <a
        //           href="${location
        //               ? `https://www.google.com/maps/search/?api=1&query=${location.t},${location.g}`
        //               : "javascript:void(0)"}"
        //           target="${location ? "_blank" : ""}">
        //           ${this.data.l.n}
        //       </a>
        //   `
    }
}
